import {
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ExternalLink,
  Lightbulb,
  Reply,
  Target,
  WandSparkles,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ResultCardId } from "./learning-chat-mock.js";

const resultIcons = {
  summary: WandSparkles,
  goal: Target,
  attention: AlertTriangle,
  prepare: BookOpen,
  keepInMind: Lightbulb,
  afterwards: Reply,
} as const;

/** Soft circular icon chips matching the Peer situation-card palette, via theme tokens. */
const resultIconTone: Record<ResultCardId, string> = {
  summary: "bg-muted text-muted-foreground",
  goal: "bg-primary text-primary-foreground",
  attention: "bg-warning text-warning-foreground",
  prepare: "bg-info text-info-foreground",
  keepInMind: "bg-accent/15 text-accent",
  afterwards: "bg-primary/15 text-primary",
};

const contextCards: ResultCardId[] = ["summary", "goal"];
const actionCards: ResultCardId[] = ["attention", "prepare", "keepInMind", "afterwards"];

export function LearningChatResultPanel({ title }: { title: string }) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<ResultCardId>("attention");

  return (
    <div className="h-full min-h-0 overflow-y-auto px-4 py-5 sm:px-6">
      <header className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-medium text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{t("learningRoute.chatResultDate")}</p>
      </header>

      <Section label={t("learningRoute.chatResult.contextGoal")}>
        {contextCards.map((id) => (
          <ResultAccordion
            id={id}
            key={id}
            open={openId === id}
            onToggle={() => setOpenId((current) => (current === id ? "attention" : id))}
          />
        ))}
      </Section>

      <Section label={t("learningRoute.chatResult.actionPlan")}>
        {actionCards.map((id) => (
          <ResultAccordion
            id={id}
            key={id}
            open={openId === id}
            onToggle={() => setOpenId((current) => (current === id ? "attention" : id))}
            warning={id === "attention"}
          />
        ))}
      </Section>

      <Section label={t("learningRoute.chatResult.courseSource")}>
        <div className="flex flex-col gap-4 rounded-2xl border border-border bg-background p-4 sm:flex-row sm:items-center">
          <div
            aria-hidden="true"
            className="relative grid h-24 w-full shrink-0 place-items-center overflow-hidden rounded-xl bg-primary/10 sm:w-40"
          >
            <BookOpen className="size-8 text-primary" strokeWidth={1.5} />
            <span className="absolute right-2 bottom-2 rounded bg-foreground/80 px-1.5 py-0.5 text-[10px] font-medium text-background">
              6:17
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-foreground">{t("learningRoute.chatResult.courseTitle")}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("learningRoute.chatResult.courseSubtitle")}
            </p>
          </div>
          <a
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-muted/25 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
            data-control-id="learning-chat.result.course"
            href="/me/passport"
          >
            {t("learningRoute.chatResult.viewCourse")}
            <ExternalLink aria-hidden="true" className="size-3.5" />
          </a>
        </div>
      </Section>
    </div>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h3 className="mb-2.5 text-xs font-medium tracking-wide text-muted-foreground uppercase">{label}</h3>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function ResultAccordion({
  id,
  open,
  onToggle,
  warning = false,
}: {
  id: ResultCardId;
  open: boolean;
  onToggle: () => void;
  warning?: boolean;
}) {
  const { t } = useTranslation();
  const Icon = resultIcons[id];
  return (
    <div
      className={`rounded-2xl border ${warning ? "border-warning-foreground/20 bg-warning/80" : "border-border bg-background"}`}
    >
      <button
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
        data-control-id={`learning-chat.result.card.${id}`}
        onClick={onToggle}
        type="button"
      >
        <span
          aria-hidden="true"
          className={`grid size-8 shrink-0 place-items-center rounded-full ${resultIconTone[id]}`}
        >
          <Icon className="size-4" strokeWidth={1.8} />
        </span>
        <span className="min-w-0 flex-1 text-sm font-medium">
          {t(`learningRoute.chatResult.cards.${id}.title`)}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <div className="border-t border-border/50 px-4 pt-3 pb-4 text-sm leading-relaxed text-foreground/90">
          {t(`learningRoute.chatResult.cards.${id}.body`)}
          {warning ? (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/70 px-2.5 py-1 text-xs text-muted-foreground">
              <BookOpen aria-hidden="true" className="size-3.5" />
              {t("learningRoute.chatResult.courseTag", { count: 2 })}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
